//====================================================================================================
//
// All formatters which are used system-wide
//
//====================================================================================================
import { useSiteInfo, type ISiteInfo } from '@/composables/useGlobalConst';
import MaintenanceIntervalService from '@/services/MaintenanceIntervalService';
export class GeneralFormatter {

  public static defaultIanaTZ: string = 'US/Eastern';
  //--------------------------------------------------------------------------------------------------
  public static getValueByPath(obj: unknown, path: string): unknown {
    return path.split(".").reduce<unknown>((value, key) => {
      if (value === null || value === undefined || typeof value !== "object") return undefined;
      return (value as Record<string, unknown>)[key];
    }, obj);
  }
  //--------------------------------------------------------------------------------------------------
  /**
   * Serialise a Date as ISO 8601 with the browser's local UTC offset
   * (e.g. "2026-05-22T00:00:00-04:00") so the .NET backend's DateTimeOffset
   * receives the user's actual offset — not UTC.
   *
   * Use this for any filter date that the backend interprets as a calendar
   * boundary. Date.prototype.toISOString() always converts to UTC and
   * silently shifts the calendar day for any non-UTC client, which causes
   * same-day inclusive boundaries to fail (see #4217).
   */
  public static toLocalISOString(date: Date): string {
    const pad = (n: number) => String(n).padStart(2, '0');
    const tzMin = -date.getTimezoneOffset();
    const sign = tzMin >= 0 ? '+' : '-';
    const absMin = Math.abs(tzMin);
    const offHH = pad(Math.floor(absMin / 60));
    const offMM = pad(absMin % 60);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
      + `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
      + `${sign}${offHH}:${offMM}`;
  }
  //--------------------------------------------------------------------------------------------------
  public static formatDateShort(date: string | Date, locale: string = 'en-US'): string {
    const _date = typeof date === 'string' ? new Date(date) : date;
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    };
    return _date.toLocaleDateString(locale, options);
    //return _date.toLocaleTimeString(locale, options);
  }
  //--------------------------------------------------------------------------------------------------
  public static formatDateWithTZ(dateInput: string | Date, timeZone: string = 'UTC', doNotIncludeTZ: boolean = false, locale: string = 'en-US'): string {

    if (!dateInput) {
      return '';
    }

    const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;

    // Preferred option (modern runtimes)
    try {
      const options: Intl.DateTimeFormatOptions = {
        timeZone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: false,
        timeZoneName: doNotIncludeTZ ? undefined : 'short' // may throw compile/time errors in old TS/runtime
      };
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      // Try extracting tz abbreviation using formatToParts (some runtimes)
      try {
        const parts = new Intl.DateTimeFormat(locale, { timeZone, timeZoneName: 'shortGeneric' }).formatToParts(date);
        const tz = parts.find(p => p.type === 'timeZoneName')?.value ?? timeZone;
        // build a readable string without relying on dateStyle/timeStyle
        const basic = new Intl.DateTimeFormat(locale, {
          timeZone,
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: false
        }).format(date);
        return doNotIncludeTZ ? basic : `${basic} ${tz}`;
      } catch {
        // Last-resort fallback: plain toLocaleString and append tz name
        try {
          const basic = date.toLocaleString(locale);
          return doNotIncludeTZ ? basic : `${basic} (${timeZone})`;
        } catch {
          // Very old environment: return ISO + timezone label
          return doNotIncludeTZ ? date.toISOString() : `${date.toISOString()} (${timeZone})`;
        }
      }
    }
  }
  //--------------------------------------------------------------------------------------------------
  public static formatDateWithoutTZ(dateInput: string | Date, timeZone: string = 'UTC', locale: string = 'en-US'): string {
    return this.formatDateWithTZ(dateInput, timeZone, true, locale);
  }
  //--------------------------------------------------------------------------------------------------
  public static formatDate(s: string, tz: string | null = null): string {

    if (!s) {
      return '';
    }
    if (!tz) {
      tz = 'America/New_York';
    }
    return GeneralFormatter.formatDateWithTZ(s, tz);
  }
  //--------------------------------------------------------------------------------------------------
  public static formatDateToSiteTimeZone(date: string | Date | null | undefined, siteCode: string, withoutTZ: boolean = false, locale: string = 'en-US'): string {

    if (!locale) {
      locale = 'en-Us';
    }

    if (!date) return "";

    const siteInfoList: ISiteInfo[] = useSiteInfo();
    const siteInfo = siteInfoList.find(
      (site) => site.siteCode?.toLowerCase() === siteCode?.toLowerCase()
    );
    const tz = siteInfo?.timeZone;

    const siteTime = GeneralFormatter.formatDateWithTZ(date, tz ?? 'America/New_York', withoutTZ, locale);
    return siteTime;
  }
  //--------------------------------------------------------------------------------------------------
  public static formatBytes(bytes: number, decimals = 1): string {
    if (!Number.isFinite(bytes)) return "—";
    if (bytes <= 0) return "0 B";

    const k = 1024;
    const units = ["B", "KB", "MB", "GB", "TB", "PB", "EB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const value = bytes / Math.pow(k, i);

    const fixed = i === 0 ? 0 : decimals; // no decimals for bytes
    return `${value.toFixed(fixed)} ${units[i]}`;
  }
  //--------------------------------------------------------------------------------------------------
  public static formatMaintFrequency = (freq: number, uom: string): string => {

    if (!freq || !uom) return '';

    let text = '';
    switch (uom) {
      case 'W':
        if (freq === 1) text = 'Weekly';
        else if (freq === 2) text = 'Bi-weekly';
        else text = `Every ${freq} weeks`;
        break;
      case 'M':
        if (freq === 1) text = 'Monthly';
        else if (freq === 2) text = 'Bi-monthly';
        else text = `Every ${freq} months`;
        break;
      case 'Q':
        if (freq === 1) text = 'Quarterly';
        else text = `Every ${freq} quarters`;
        break;
      case 'Y':
        if (freq === 1) text = 'Annually';
        else text = `Every ${freq} years`;
        break;
      default:
        text = `${freq} ${uom}`;
    }

    return text ? `(${text})` : '';
  };

  //   //--------------------------------------------------------------------------------------------------
  //   /**
  //    * Convert Windows timezone ID to an IANA timezone name using the `windows-iana` package when available.
  //    * Falls back to a small built-in mapping or `defaultIanaTZ`.
  //    * @param windowsTZ Windows timezone string (e.g. 'Eastern Standard Time')
  //    * @param region optional region hint (e.g. 'US')
  //    */
  //   public static windowsToIana(windowsTZ: string | null | undefined, region?: string): string {
  //     if (!windowsTZ) return GeneralFormatter.defaultIanaTZ;

  //     // Use the imported `winIana` module (if available) and handle common API shapes.
  //     try {
  //       const mod: any = winIana as any;
  //       if (mod) {
  //         if (typeof mod.find === 'function') {
  //           const r = mod.find(windowsTZ, region);
  //           if (r) return Array.isArray(r) ? r[0] : r;
  //         }
  //         if (typeof mod.findIana === 'function') {
  //           const r = mod.findIana(windowsTZ, region);
  //           if (r) return Array.isArray(r) ? r[0] : r;
  //         }
  //         if (typeof mod.default === 'function') {
  //           const r = mod.default(windowsTZ, region);
  //           if (r) return Array.isArray(r) ? r[0] : r;
  //         }
  //         // fallback: some packages expose a mapping object
  //         const map = mod.mapping ?? mod.map ?? mod;
  //         if (map && map[windowsTZ]) {
  //           const entry = map[windowsTZ];
  //           return Array.isArray(entry) ? entry[0] : entry;
  //         }
  //       }
  //     } catch (err) {
  //       // ignore and use fallback mapping
  //     }

  //     // Expanded built-in fallback map for many common Windows timezone IDs
  //     const fallback: Record<string, string> = {
  //       // UTC / GMT
  //       'UTC': 'Etc/UTC',
  //       'Coordinated Universal Time': 'Etc/UTC',
  //       'GMT Standard Time': 'Europe/London',
  //       'Greenwich Standard Time': 'Europe/London',

  //       // North America
  //       'Eastern Standard Time': 'America/New_York',
  //       'US Eastern Standard Time': 'America/New_York',
  //       'Eastern Standard Time (Mexico)': 'America/Cancun',
  //       'Central Standard Time': 'America/Chicago',
  //       'Central America Standard Time': 'America/Guatemala',
  //       'Central Standard Time (Mexico)': 'America/Mexico_City',
  //       'Mountain Standard Time': 'America/Denver',
  //       'Mountain Standard Time (Mexico)': 'America/Chihuahua',
  //       'Arizona': 'America/Phoenix',
  //       'US Mountain Standard Time': 'America/Boise',
  //       'Pacific Standard Time': 'America/Los_Angeles',
  //       'Pacific Standard Time (Mexico)': 'America/Tijuana',
  //       'Alaskan Standard Time': 'America/Anchorage',
  //       'Hawaiian Standard Time': 'Pacific/Honolulu',

  //       // South America
  //       'SA Pacific Standard Time': 'America/Bogota',
  //       'Pacific SA Standard Time': 'America/Santiago',
  //       'Argentina Standard Time': 'America/Buenos_Aires',
  //       'E. South America Standard Time': 'America/Sao_Paulo',

  //       // Europe
  //       'W. Europe Standard Time': 'Europe/Berlin',
  //       'Romance Standard Time': 'Europe/Paris',
  //       'Central Europe Standard Time': 'Europe/Warsaw',
  //       'GTB Standard Time': 'Europe/Bucharest',
  //       'FLE Standard Time': 'Europe/Kiev',

  //       // Middle East / Africa
  //       'Arab Standard Time': 'Asia/Riyadh',
  //       'Israel Standard Time': 'Asia/Jerusalem',
  //       'Turkey Standard Time': 'Europe/Istanbul',
  //       'South Africa Standard Time': 'Africa/Johannesburg',
  //       'Morocco Standard Time': 'Africa/Casablanca',

  //       // Russia / CIS
  //       'Russian Standard Time': 'Europe/Moscow',

  //       // Asia
  //       'India Standard Time': 'Asia/Kolkata',
  //       'Pakistan Standard Time': 'Asia/Karachi',
  //       'Bangladesh Standard Time': 'Asia/Dhaka',
  //       'Sri Lanka Standard Time': 'Asia/Colombo',
  //       'SE Asia Standard Time': 'Asia/Bangkok',
  //       'Myanmar Standard Time': 'Asia/Rangoon',
  //       'N. Central Asia Standard Time': 'Asia/Novosibirsk',
  //       'China Standard Time': 'Asia/Shanghai',
  //       'Taipei Standard Time': 'Asia/Taipei',
  //       'Tokyo Standard Time': 'Asia/Tokyo',
  //       'Korea Standard Time': 'Asia/Seoul',
  //       'Singapore Standard Time': 'Asia/Singapore',

  //       // Oceania
  //       'AUS Eastern Standard Time': 'Australia/Sydney',
  //       'AUS Central Standard Time': 'Australia/Darwin',
  //       'E. Australia Standard Time': 'Australia/Brisbane',
  //       'W. Australia Standard Time': 'Australia/Perth',
  //       'New Zealand Standard Time': 'Pacific/Auckland',

  //       // Misc / others
  //       'Pacific/Fiji': 'Pacific/Fiji'
  //     };

  //     return fallback[windowsTZ] ?? GeneralFormatter.defaultIanaTZ;
  //   }
  //   //--------------------------------------------------------------------------------------------------

  /**
   * Format frequency + UOM into a human-readable string.
   * E.g. (1, 'M') → 'Monthly', (2, 'W') → 'Bi-weekly', (3, 'Y') → 'Every 3 years'
   *
   * Sourced from the seeded lookup's Description (Coda child #3 R2); the switch below is the
   * documented fallback (R5) for an unloaded lookup or an unseeded cadence. The seed was aligned to
   * this exact wording, so both paths agree (R4).
   *
   * This is the formatter behind the Tasks list, Task Details, PM Schedules, Create Work Order and
   * group-ops — i.e. most of child #3's named consumers, even though the article lists only
   * frequencyLabels.ts and the backend FrequencyFormatter.
   */
  public static formatFrequency(frequency: number | null | undefined, uom: string | null | undefined): string {
    const freq = Number(frequency || 0);
    const u = (uom ?? '').toUpperCase();
    if (!freq || !u) return '';

    const description = MaintenanceIntervalService.find(freq, u)?.description?.trim();
    if (description) return description;

    switch (u) {
      case 'W':
        if (freq === 1) return 'Weekly';
        if (freq === 2) return 'Bi-weekly';
        return `Every ${freq} weeks`;
      case 'M':
        if (freq === 1) return 'Monthly';
        if (freq === 2) return 'Bi-monthly';
        return `Every ${freq} months`;
      case 'Q':
        if (freq === 1) return 'Quarterly';
        return `Every ${freq} quarters`;
      case 'Y':
        if (freq === 1) return 'Annually';
        return `Every ${freq} years`;
      case 'D':
        if (freq === 1) return 'Daily';
        return `Every ${freq} days`;
      default:
        return `${freq} ${u}`;
    }
  }
  //--------------------------------------------------------------------------------------------------
}
// EOF
